import { useCallback, useContext, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addReadingToListThunk, deleteReadingFromList } from '@/redux/slices/readingsResumesSlice';
import { generateColor } from '@/utils/DashboardGraphs';
import { useDashboardDataContext, getGlobalSourcesInitialized, setGlobalSourcesInitialized, clearGlobalSourcesInitialized } from '../components/Dashboard/DashboardDataContext';
import ParameterService from '@/domain/Parameter/ParameterService';
import DashboardContext from '@/contexts/DashboardContext';

const parameterService = new ParameterService();

interface UseOtherSourcesByInfraProps {
	toggleSeriesVisibility: (record: any, visibilityState: any, setVisibilityState: any) => void;
	fetchHealthDataByParameterId: (parameterId: number) => Promise<any>;
	setSourceToAnnotation?: (source: any) => void;
	setAnnotationMode?: (mode: boolean) => void;
	scrollToTop?: (ref: any) => void;
	topRef?: any;
	handleGetAnnotations?: (sourceId: string, parameterId: string, serialNumber: string, isRecommended?: boolean) => Promise<void>;
}

export const useOtherSourcesByInfra = ({
	toggleSeriesVisibility,
	fetchHealthDataByParameterId,
	setSourceToAnnotation,
	setAnnotationMode,
	scrollToTop,
	topRef,
	handleGetAnnotations,
}: UseOtherSourcesByInfraProps) => {
	const dispatch = useDispatch();
	const dashboardDataContext = useDashboardDataContext();
	const dashboardContext = useContext(DashboardContext);

	// Ref always holds the latest selectedDate — prevents stale closure in onCheck handlers stored in state
	const selectedDateRef = useRef(dashboardContext.value.selectedDate);
	useEffect(() => {
		selectedDateRef.current = dashboardContext.value.selectedDate;
	}, [dashboardContext.value.selectedDate]);
	const getOtherSourcesByInfra = useCallback(
		async (parameter: any) => {
			try {
				const response = (await parameterService.getParameter(parameter.parameterId)) as any;
				// const healthDataByParameterId = await fetchHealthDataByParameterId(Number(parameter.parameterId));
				const healthDataByParameterId: any = null;
				const parameterName = response.name || 'Unknown Parameter';
				
				let infrastructureName = 'Infrastructure';
				
				if (parameter.infrastructureName && parameter.infrastructureName !== 'Infrastructure') {
					infrastructureName = parameter.infrastructureName;
				}
				
				
				dashboardDataContext.setSourcesByParam((currentSources: any[]) => {
					// Check if this parameter already exists in the sources list
					const existingParamIndex = currentSources.findIndex(
						(param) => param.id === parameter.parameterId,
					);

					// Get existing source states if available
					const existingSources =
						existingParamIndex >= 0 ? currentSources[existingParamIndex].sources : [];

					// Create recommended source with proper state preservation
					const existingRecommended = existingSources.find(
						(s) => s.name === 'Recommended' && s.sourceId === null,
					);
					const recommendedChecked = existingRecommended ? existingRecommended.checked : true;
					// Sync sourcesInitialized with the actual checked state for recommended source
					const recommendedKey = `recommended-${parameter.parameterId}`;
					setGlobalSourcesInitialized(recommendedKey, recommendedChecked);
					
					const recommendedSource = {
						name: 'Recommended',
						sourceId: null,
						parameterId: parameter.parameterId,
						serialNumber: null,
						color: generateColor(parameter.parameterId, null),
						tooltip: 'Recommended',
						checked: recommendedChecked,
						_handlerAttached: true,
						onCheck: (checked: boolean, sourceId: number, serialNumber: string) => {
							const sourceKey = `recommended-${parameter.parameterId}`;
							// Remove the same state check - let debouncing handle duplicates
							setGlobalSourcesInitialized(sourceKey, checked);

							// Update the checked state in the context
							dashboardDataContext.setSourcesByParam((sources) => {
								return sources.map((paramSource) => {
									if (paramSource.id === parameter.parameterId) {
										const updatedSources = paramSource.sources.map((src) => {
											if (src.name === 'Recommended' && src.sourceId === null) {
												return { ...src, checked };
											}
											return src;
										});
										
										// ✅ Check if at least one source is checked
										const hasAtLeastOneChecked = updatedSources.some(src => src.checked);
										const noSourcesChecked = updatedSources.every(src => !src.checked);
										
										// ✅ Update parent checkbox: active if at least one source is checked
										dashboardDataContext.setSelectedRowKeys((prevKeys) => {
											const tableKey = parameter.parameterId;
											const isAlreadySelected = prevKeys.includes(tableKey);
											
											if (hasAtLeastOneChecked && !isAlreadySelected) {
												return [...prevKeys, tableKey];
											} else if (noSourcesChecked && isAlreadySelected) {
												return prevKeys.filter(key => key !== tableKey);
											}
											return prevKeys;
										});
										return {
											...paramSource,
											sources: updatedSources,
										};
									}
									return paramSource;
								});
							});

							// Update the table selection when Recommended source is clicked
							dashboardDataContext.toggleSeriesVisibility(parameter);
							
							// Force update the global state to match the new visibility
							setTimeout(() => {
								setGlobalSourcesInitialized(sourceKey, checked);
							}, 100); // After debounce completes
							
							if (checked) {
								const [dateFrom, dataTo] = dashboardContext.value.selectedDate;
								dashboardDataContext.setSelectedParamsList((selectedParams: any[]) => {
									const copy = [...selectedParams];
									const indexFinded = copy.findIndex(
										(param) => param.parameterId == parameter.parameterId,
									);
									if (indexFinded >= 0) {
										copy[indexFinded].sourceList = [
											...copy[indexFinded].sourceList,
											{
												sourceId,
												serialNumber,
											},
										];
									}
									return copy;
								});
								
								// ❌ REMOVED: Duplicate call to handleGetAnnotations
								// useAnnotations hook already handles this reactively via readingsList
							} else {
								if (
									dashboardDataContext.selectedSources.indexOf(
										(record) => record.serialNumberMeasurementTool == serialNumber,
									) < 0
								) {
									// dispatch(
									// 	deleteReadingFromList(parameter.parameterId, serialNumber) as any,
									// );
									toggleSeriesVisibility(parameter, dashboardDataContext.seriesVisibilityState, dashboardDataContext.setSeriesVisibilityState);
								}
								dashboardDataContext.setSelectedParamsList((selectedParams: any[]) => {
									const copy = [...selectedParams];
									const indexFinded = copy.findIndex(
										(param) => param.parameterId == parameter.parameterId,
									);
									copy[indexFinded].sourceList = copy[indexFinded].sourceList.filter(
										(sl) => sl.serialNumber !== serialNumber,
									);
									return copy;
								});
							}

						},
					};

					// Map response sources with state preservation
					const mappedSources = response.sources.map((source: any) => {
						// Find if this source already exists to preserve its checked state
						const existingSource = existingSources.find(
							(s) => s.serialNumber === source.serialNumber,
						);
						const isChecked = existingSource ? existingSource.checked : false;
						
						// Sync sourcesInitialized with the actual checked state to prevent desync
						const sourceKey = `source-${parameter.parameterId}-${source.serialNumber}`;
						setGlobalSourcesInitialized(sourceKey, isChecked);

						return {
							...source,
							color: generateColor(parameter.parameterId, source.serialNumber),
							parameterId: parameter.parameterId,
							tooltip: source.name,
							checked: isChecked,
							showAnnotationIcon: true,
							_handlerAttached: true,
							onCheck: (checked: boolean, sourceId: number, serialNumber: string) => {
								const sourceKey = `source-${parameter.parameterId}-${source.serialNumber}`;
								if (getGlobalSourcesInitialized(sourceKey) === checked) {
									return;
								}
								setGlobalSourcesInitialized(sourceKey, checked);

								// Update the checked state in the context
								dashboardDataContext.setSourcesByParam((sources) => {
									return sources.map((paramSource) => {
										if (paramSource.id === parameter.parameterId) {
											const updatedSources = paramSource.sources.map((src) => {
												if (src.serialNumber === source.serialNumber) {
													return { ...src, checked };
												}
												return src;
											});
											
											// Check if at least one source is checked
											const hasAtLeastOneChecked = updatedSources.some(src => src.checked);
											const noSourcesChecked = updatedSources.every(src => !src.checked);
											
											// Update parent checkbox: active if at least one source is checked
											dashboardDataContext.setSelectedRowKeys((prevKeys) => {
												const tableKey = parameter.parameterId;
												const isAlreadySelected = prevKeys.includes(tableKey);
												
												if (hasAtLeastOneChecked && !isAlreadySelected) {
													return [...prevKeys, tableKey];
												} else if (noSourcesChecked && isAlreadySelected) {
													return prevKeys.filter(key => key !== tableKey);
												}
												return prevKeys;
											});
											
											return {
												...paramSource,
												sources: updatedSources,
											};
										}
										return paramSource;
									});
								});

								if (checked) {
									const [dateFrom, dataTo] = selectedDateRef.current;
									dispatch(
										addReadingToListThunk(
											parameter.parameterId,
											source.serialNumber,
											dateFrom,
											dataTo,
											source.sourceId,
											true,
										) as any,
									);
									dashboardDataContext.setSelectedParamsList((selectedParams: any[]) => {
										const copy = [...selectedParams];
										const indexFinded = copy.findIndex(
											(param) => param.parameterId == parameter.parameterId,
										);
										if (indexFinded >= 0) {
											copy[indexFinded].sourceList = [
												...copy[indexFinded].sourceList,
												{
													sourceId: source.sourceId,
													serialNumber: source.serialNumber,
												},
											];
										}
										return copy;
									});
								} else {
									dispatch(
										deleteReadingFromList(parameter.parameterId, source.serialNumber) as any,
									);
									dashboardDataContext.setSelectedParamsList((selectedParams: any[]) => {
										const copy = [...selectedParams];
										const indexFinded = copy.findIndex(
											(param) => param.parameterId == parameter.parameterId,
										);
										copy[indexFinded].sourceList = copy[indexFinded].sourceList.filter(
											(sl) => sl.serialNumber !== source.serialNumber,
										);
										return copy;
									});
								}
							},
							onAddAnnotation: setSourceToAnnotation && setAnnotationMode && scrollToTop && topRef ? (source: any) => {
								setSourceToAnnotation({
									...source,
									parameterId: parameter.parameterId,
								});
								setAnnotationMode(true);
								scrollToTop(topRef);
							} : undefined,
						};
					});


					const updatedSources = {
						id: parameter.parameterId,
						name: `${infrastructureName} | ${parameterName} | ${response.sources.length} sources`,
						usage: parameter.usage,
						health: healthDataByParameterId
							? healthDataByParameterId?.data[0] ? healthDataByParameterId?.data[0].sourceHealth
							: 0 : 0,//TODO: Change this when health data is available
						sources: [recommendedSource, ...mappedSources],
					};

					// ✅ FIX: If parameter already exists, replace it; otherwise, add it
					if (existingParamIndex >= 0) {
						const newSources = [...currentSources];
						newSources[existingParamIndex] = updatedSources;
						return newSources;
					} else {
						return [...currentSources, updatedSources];
					}
				});
			} catch (error) {
				console.error('Error fetching parameter sources:', error);
			}
		},
	[
		dashboardDataContext.setSourcesByParam,
		dashboardDataContext.selectedSources,
		dashboardDataContext.seriesVisibilityState,
		dashboardDataContext.setSeriesVisibilityState,
		dashboardDataContext.setSelectedParamsList,
		dashboardDataContext.selectedParamsList,
		dashboardDataContext.selectedRowKeys,
		dashboardDataContext.setSelectedRowKeys,
		dashboardContext.value.selectedDate,
		dispatch,
		fetchHealthDataByParameterId,
		toggleSeriesVisibility,
		setSourceToAnnotation,
		setAnnotationMode,
		scrollToTop,
		topRef,
	],
);

	const clearSourcesInitialized = useCallback((parameterId: number) => {
		clearGlobalSourcesInitialized(parameterId);
	}, []);

	const updateInfrastructureName = useCallback((parameterId: number, newInfrastructureName: string) => {
		if (!newInfrastructureName || newInfrastructureName === 'Infrastructure') {
			return;
		}

		dashboardDataContext.setSourcesByParam((currentSources) => {
			return currentSources.map((source) => {
				if (source.id === parameterId && source.name.includes('Infrastructure')) {
					const nameParts = source.name.split(' | ');
					const parameterName = nameParts[1] || 'Parameter';
					const sourcesCount = nameParts[2] || '0 sources';
					
					return {
						...source,
						name: `${newInfrastructureName} | ${parameterName} | ${sourcesCount}`,
					};
				}
				return source;
			});
		});
	}, [dashboardDataContext.setSourcesByParam]);

	return { 
		getOtherSourcesByInfra, 
		clearSourcesInitialized,
		getGlobalSourcesInitialized,
		setGlobalSourcesInitialized,
		updateInfrastructureName
	};
};
